/**
 * Pure Insights display helpers (insights-demo-01). No I/O except the
 * remembered range, which tolerates storage being unavailable — unit-tested.
 */
import { InsightsRange, InsightsWeek } from '../types';

export const RANGES: { key: InsightsRange; label: string }[] = [
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'all', label: 'All time' },
];
export const DEFAULT_RANGE: InsightsRange = '90d';
export const RANGE_STORAGE_KEY = 'insights.range';

/** A valid range from a URL/storage value, or null. */
export function parseRange(value: string | null | undefined): InsightsRange | null {
  return RANGES.some((r) => r.key === value) ? (value as InsightsRange) : null;
}

/** The last range this browser chose, or null (storage may be blocked). */
export function rememberedRange(): InsightsRange | null {
  try {
    return parseRange(localStorage.getItem(RANGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function rememberRange(range: InsightsRange): void {
  try {
    localStorage.setItem(RANGE_STORAGE_KEY, range);
  } catch {
    /* private mode / blocked storage — the URL still carries the range */
  }
}

/** URL first, then this browser's last choice, then the default. */
export function resolveRange(fromUrl: string | null): InsightsRange {
  return parseRange(fromUrl) ?? rememberedRange() ?? DEFAULT_RANGE;
}

export function formatInt(n: number): string {
  return n.toLocaleString('en-US');
}

/** 80 → "80%", 12.6 → "12.6%". */
export function formatPct(p: number): string {
  return `${Number.isInteger(p) ? p.toFixed(0) : p.toFixed(1)}%`;
}

/** 100 → "100 h", 56.8 → "56.8 h". */
export function formatHours(h: number): string {
  return `${Number.isInteger(h) ? h.toFixed(0) : h.toFixed(1)} h`;
}

/** Bar length in % of the largest value; 0..100, never NaN. */
export function barPct(value: number, max: number): number {
  if (!max || max <= 0 || value <= 0) return 0;
  return Math.min(100, (value / max) * 100);
}

/** "2026-09-07" → "Sep 7" (dates are UTC week starts). */
export function weekLabel(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export type WeekSegment = 'human' | 'voicemail' | 'switchboard' | 'dead' | 'other';
export const WEEK_SEGMENTS: { key: WeekSegment; label: string }[] = [
  { key: 'human', label: 'Human' },
  { key: 'voicemail', label: 'Voicemail' },
  { key: 'switchboard', label: 'Switchboard' },
  { key: 'dead', label: 'Dead / fax' },
  { key: 'other', label: 'Busy / no answer / unknown' },
];

/** Each segment's share of the bar height, in %, relative to the tallest week. */
export function stackHeights(week: InsightsWeek, tallest: number): { key: WeekSegment; pct: number; count: number }[] {
  return WEEK_SEGMENTS.map(({ key }) => ({ key, count: week[key], pct: barPct(week[key], tallest) }));
}

/** Polyline points in a 0..100 box for a rising series (y is flipped). */
export function linePoints(values: number[]): string {
  if (values.length === 0) return '';
  const max = Math.max(...values);
  const step = values.length > 1 ? 100 / (values.length - 1) : 0;
  return values
    .map((v, i) => {
      const x = values.length > 1 ? i * step : 50;
      const y = 100 - barPct(v, max);
      return `${round2(x)},${round2(y)}`;
    })
    .join(' ');
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Grade → colour token. A/B good, C caution, D/F poor. */
export function gradeTone(grade: string | null): 'good' | 'warn' | 'bad' | 'none' {
  if (grade === 'A' || grade === 'B') return 'good';
  if (grade === 'C') return 'warn';
  if (grade === 'D' || grade === 'F') return 'bad';
  return 'none';
}
